import { useQuery, useMutation } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export interface InvoicePaymentItem {
  id: number;
  invoiceId: number;
  userId: number;
  amount: number;
  paymentDate: string;
  notes: string | null;
  paymentMethod: string | null;
  source: string | null;
  receiptSentAt: string | null;
  createdAt: string;
}

export interface RecordPaymentBody {
  amount: number;
  paymentDate?: string;
  notes?: string | null;
  paymentMethod?: string | null;
}

export interface RecordPaymentResult {
  payment: InvoicePaymentItem;
  invoiceStatus: string;
  totalPaid: number;
  balanceOwing: number;
}

export const getListInvoicePaymentsQueryKey = (invoiceId: number) =>
  [`/api/invoices/${invoiceId}/payments`] as const;

export const listInvoicePayments = async (invoiceId: number): Promise<InvoicePaymentItem[]> =>
  customFetch<InvoicePaymentItem[]>(`/api/invoices/${invoiceId}/payments`);

export const useListInvoicePayments = <
  TData = InvoicePaymentItem[],
  TError = ErrorType<unknown>
>(
  invoiceId: number,
  options?: {
    query?: UseQueryOptions<InvoicePaymentItem[], TError, TData>;
  }
) => {
  const { query: queryOptions } = options ?? {};
  const queryKey = queryOptions?.queryKey ?? getListInvoicePaymentsQueryKey(invoiceId);

  return useQuery<InvoicePaymentItem[], TError, TData>({
    queryKey,
    queryFn: () => listInvoicePayments(invoiceId),
    enabled: !!invoiceId,
    ...queryOptions,
  });
};

export const recordInvoicePayment = async (
  invoiceId: number,
  data: RecordPaymentBody
): Promise<RecordPaymentResult> =>
  customFetch<RecordPaymentResult>(`/api/invoices/${invoiceId}/payments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const useRecordInvoicePayment = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    RecordPaymentResult,
    TError,
    { invoiceId: number; data: RecordPaymentBody },
    TContext
  >
) => {
  return useMutation<
    RecordPaymentResult,
    TError,
    { invoiceId: number; data: RecordPaymentBody },
    TContext
  >({
    mutationFn: ({ invoiceId, data }) => recordInvoicePayment(invoiceId, data),
    ...options,
  });
};

export interface DeletePaymentResult {
  invoiceStatus: string;
  totalPaid: number;
  balanceOwing: number;
}

export const deleteInvoicePayment = async (
  invoiceId: number,
  paymentId: number
): Promise<DeletePaymentResult> =>
  customFetch<DeletePaymentResult>(`/api/invoices/${invoiceId}/payments/${paymentId}`, {
    method: "DELETE",
  });

export const useDeleteInvoicePayment = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    DeletePaymentResult,
    TError,
    { invoiceId: number; paymentId: number },
    TContext
  >
) => {
  return useMutation<
    DeletePaymentResult,
    TError,
    { invoiceId: number; paymentId: number },
    TContext
  >({
    mutationFn: ({ invoiceId, paymentId }) => deleteInvoicePayment(invoiceId, paymentId),
    ...options,
  });
};

export const sendPaymentReceipt = async (
  invoiceId: number,
  paymentId?: number,
  ccSelf?: boolean
): Promise<{ success: boolean; to: string }> =>
  customFetch<{ success: boolean; to: string }>(
    `/api/invoices/${invoiceId}/send-receipt`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...(paymentId ? { paymentId } : {}), ...(ccSelf ? { ccSelf } : {}) }),
    }
  );

export const useSendPaymentReceipt = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    { success: boolean; to: string },
    TError,
    { invoiceId: number; paymentId?: number; ccSelf?: boolean },
    TContext
  >
) => {
  return useMutation<
    { success: boolean; to: string },
    TError,
    { invoiceId: number; paymentId?: number; ccSelf?: boolean },
    TContext
  >({
    mutationFn: ({ invoiceId, paymentId, ccSelf }) => sendPaymentReceipt(invoiceId, paymentId, ccSelf),
    ...options,
  });
};
